import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/authContext";
import { baseAxios } from "../apis";

export const useStrategyListsQuery = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["strategyLists"],
    queryFn: () =>
      baseAxios
        .get("/api/strategy-lists")
        .then((res) => res.data.bot_master_list),
    enabled: !!user,
    staleTime: Infinity,
  });
};

export const useBotListsQuery = ({ enabled = true } = {}) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["botLists"],
    queryFn: () =>
      baseAxios.get("/api/bot_list").then((res) => res.data.bot_lists),
    enabled: enabled && !!user,  
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useBotDetailQuery = (botId) => {
  const { user } = useAuth();
  const { data: strategyLists } = useStrategyListsQuery();
  return useQuery({
    queryKey: ["botDetail", botId],
    queryFn: async () => {
      const { data } = await baseAxios.get(`/api/bot_detail/${botId}`);
      const foundStrategy = strategyLists?.find(
        (strg) => strg.bot_name === data.bot_detail.strategy_name,
      );
      return {
        botDetail: { ...data.bot_detail, setting: data?.setting },
        dataChart: data?.graph_data || [],
        trends: data?.bot_profit_trends?.trends || [],
        foundStrategy,
      };
    },
    enabled: !!botId && !!strategyLists && !!user,
  });
};

const formatExchangeName = (exchangeName) => {
  if (!exchangeName) return "Unknown";

  const normalized = exchangeName.toLowerCase().replace(/_/g, " ");

  const specialCases = {
    "coins ph": "Coins.ph",
    osl: "OSL",
  };

  const specialMatch = Object.keys(specialCases).find((key) =>
    normalized.startsWith(key),
  );
  if (specialMatch) return specialCases[specialMatch];

  const upperWords = ["okx", "osl"];
  return normalized
    .split(" ")
    .map((word) =>
      upperWords.some((u) => word.startsWith(u))
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(" ");
};

export const useExchangesQuery = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["exchanges"],
    queryFn: async () => {
      const { data } = await baseAxios.get("/api/my-exchanges");
      const myExchangesMap = new Map(
        data.my_exchanges.map((ex) => [ex.exchange.toLowerCase(), ex]),
      );

      // Find which OSL variant the user has connected (has user_api_settings)
      const oslVariants = ["osl_indonesia", "osl_global", "osl"];
      const connectedOslKey = oslVariants.find(
        (key) => (myExchangesMap.get(key)?.user_api_settings?.length ?? 0) > 0,
      );

      const formattedExchanges = data.available_exchanges
        .filter((exchange) => {
          const key = exchange.exchange.toLowerCase();
          // If it's an OSL variant, only show it if it's the connected one,
          // or show the first OSL in the list if none are connected
          if (oslVariants.includes(key)) {
            return connectedOslKey
              ? key === connectedOslKey
              : key ===
                  oslVariants.find((v) =>
                    data.available_exchanges.some(
                      (e) => e.exchange.toLowerCase() === v,
                    ),
                  );
          }
          return true;
        })
        .map((exchange) => {
          const myExchange = myExchangesMap.get(
            exchange.exchange.toLowerCase(),
          );
          return {
            id: exchange.id,
            name: formatExchangeName(exchange.exchange), // ✅ fixed
            connected: !!myExchange?.user_api_settings?.length > 0,
            key: exchange.exchange.toLowerCase(),
            balance: myExchange?.balance || 0,
            user_api_settings: myExchange?.user_api_settings,
            local_currency: myExchange?.local_currency,
            estimated_usd_balance: myExchange?.estimated_usd_balance,
          };
        })
        .sort(
          (a, b) => b.connected - a.connected || a.name.localeCompare(b.name),
        );

      const connectedExchanges = Array.from(myExchangesMap.values())
        .filter((ex) => !!ex.user_api_settings?.length)
        .map((ex) => ({
          ...ex,
          name: formatExchangeName(ex.exchange),
          key: ex.exchange.toLowerCase(),
          connected: ex.user_api_settings?.length ?? 1, // OSL may not have array length
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      console.log("connectedExchanges", connectedExchanges);

      return { exchanges: formattedExchanges, myExchanges: connectedExchanges };
    },
    enabled: !!user,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

export const useConvertDataQuery = (botId) => {
  return useQuery({
    queryKey: ["convertData", botId],
    queryFn: () =>
      baseAxios
        .get(`/api/stop_bot/convert_details/${botId}`)
        .then((res) => res.data),
    enabled: !!botId,
    retry: 2,
  });
};

export const useBotProfileQuery = (strategyId, pairId) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["botProfile", strategyId, pairId],
    queryFn: async () => {
      const { data } = await baseAxios.get(
        `/api/bot_profile?bot_master_id=${strategyId}&coin_id=${pairId}`,
      );

      return data.parameters_grouped;
    },
    refetchOnWindowFocus: false,
    enabled: !!strategyId && !!user && !!pairId,
    // Add this line to set the number of retries to 2
    retry: false,
  });
};

export const useBalanceQuery = (
  selectedPair,
  selectedExchange = null,
  isDemo = false,
) => {
  const { user } = useAuth();

  const coin = selectedPair?.coin;
  const base = selectedPair?.base_currency;
  const exchangeId = selectedExchange?.id;

  return useQuery({
    queryKey: ["balance", user?.id, selectedPair?.id, exchangeId, isDemo],

    queryFn: async () => {
      const apiPath = isDemo
        ? "/api/demo/bot_create/balance"
        : "/api/bot_create/balance";

      const { data } = await baseAxios.get(
        `${apiPath}?coin=${coin}&base=${base}&exchange=${exchangeId}`,
      );

      const allExchanges = (data?.my_exchanges || []).map((ex) => ({
        ...ex,
        name: formatExchangeName(ex.exchange),
      }));

      if (exchangeId) {
        return allExchanges.find((ex) => ex.id === exchangeId) || null;
      }

      return null;
    },

    enabled: !!user && !!coin && !!base,
  });
};
